import { useEffect, useState } from "react";
import { FileText, Send, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const T = {
  el: { label: "Βαθμολογούμενη εργασία", submit: "Υποβολή εργασίας", resubmit: "Νέα υποβολή",
    placeholder: "Γράψε εδώ την εργασία σου (τουλάχιστον 200 χαρακτήρες): παραδοτέα, υπολογισμοί, συμπεράσματα και προτάσεις εφαρμογής.",
    pending: "Υποβλήθηκε — αναμένει βαθμολόγηση", graded: "Βαθμολογήθηκε", feedback: "Σχόλια mentor",
    sent: "Η εργασία υποβλήθηκε!", chars: "χαρακτήρες", lockedHint: "Εγγράψου στο πρόγραμμα για να υποβάλεις την εργασία." },
  en: { label: "Graded assignment", submit: "Submit assignment", resubmit: "Resubmit",
    placeholder: "Write your assignment here (at least 200 characters): deliverables, calculations, conclusions and application proposals.",
    pending: "Submitted — awaiting grading", graded: "Graded", feedback: "Mentor feedback",
    sent: "Assignment submitted!", chars: "characters", lockedHint: "Enrol in the programme to submit the assignment." },
};

export const AssignmentPanel = ({ courseId, module, enrolled }) => {
  const { lang } = useLang();
  const tr = T[lang] || T.el;
  const [sub, setSub] = useState(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!enrolled) return;
    api.get(`/programs/${courseId}/assignments/my`)
      .then(({ data }) => {
        const mine = (data || []).find((a) => a.module === module);
        if (mine) {
          setSub(mine);
          setText(mine.text);
        }
      })
      .catch(() => {});
  }, [courseId, module, enrolled]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/programs/${courseId}/assignments`, { module, text });
      setSub(data);
      setOpen(false);
      toast.success(tr.sent);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (!enrolled) {
    return <p className="text-xs text-[#64748B] mt-3" data-testid={`assignment-locked-${module}`}>{tr.lockedHint}</p>;
  }

  return (
    <div className="mt-4 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] p-4" data-testid={`assignment-panel-${module}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8] flex items-center gap-2">
        <FileText className="w-3.5 h-3.5" /> {tr.label}
      </p>

      {sub && (
        <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`assignment-status-${module}`}>
          {sub.status === "graded" ? (
            <>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1D4ED8] text-white text-[11px] font-bold uppercase tracking-wider">
                <CheckCircle2 className="w-3.5 h-3.5" /> {tr.graded}
              </span>
              <span className="font-heading font-black text-2xl text-[#0B1B3A]" data-testid={`assignment-grade-${module}`}>{Math.round(sub.grade)}/100</span>
            </>
          ) : (
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F1F5F9] text-[#475569] text-[11px] font-bold uppercase tracking-wider">
              <Clock className="w-3.5 h-3.5" /> {tr.pending}
            </span>
          )}
        </div>
      )}

      {sub?.feedback && (
        <div className="mt-3 rounded-xl bg-white border border-[#E2E8F0] p-4" data-testid={`assignment-feedback-${module}`}>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{tr.feedback}</p>
          <p className="text-sm text-[#334155] mt-1 whitespace-pre-line">{sub.feedback}</p>
        </div>
      )}

      {open || !sub ? (
        <div className="mt-3">
          <textarea data-testid={`assignment-text-${module}`} rows={6} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={tr.placeholder} className="input-cream w-full text-sm" />
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <button data-testid={`assignment-submit-${module}`} disabled={sending || text.trim().length < 200} onClick={send}
              className="btn-blue !px-5 !py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {tr.submit}
            </button>
            <span className="text-xs text-[#64748B]">{text.trim().length} {tr.chars}</span>
          </div>
        </div>
      ) : (
        <button data-testid={`assignment-reopen-${module}`} onClick={() => setOpen(true)} className="btn-outline-light !px-4 !py-1.5 text-xs mt-3">
          {tr.resubmit}
        </button>
      )}
    </div>
  );
};

export default AssignmentPanel;
